import { Currency } from 'dinero.js';
import { IDictionary, IGalleryTag } from '.';

export interface IPricingListItem {
  createdAt: string;
  name: string;
  taxRate: number;
  updatedAt: string;
  __v: number;
  _id: string;
  _laboratory: string;
  _user: string;
  default?: boolean;
  selected?: boolean;
}

export interface IPricingGroup {
  _user: string;
  _laboratory: string;
  _pricingList: string;
  _productGroup: string;
  id: string;
  active: boolean;
  favorite: boolean;
}
export interface IFetchPricingGroupAction {
  id?: string;
}

export interface IFetchPricingListAction {
  id?: string;
}

export interface IProductDimensions {
  height: number;
  width: number;
  depth: number;
  unit: 'cm';
}

export interface IProductImage {
  type: string;
  src: string;
  order: number;
}

export enum ShopOptionsEnum {
  Border = 'border',
  Finish = 'finish',
  BorderColor = 'border-color',
  Color = 'color',
  Surface = 'surface',
  ColorCombination = 'color-combination',
  Material = 'material'
}

export type TOptionType = typeof ShopOptionsEnum;

export interface IOption {
  _id: string;
  name: string;
  type: TOptionType;
  default: boolean;
}

export interface IPerfectFor {
  icon: string;
  text: string;
}

export interface ICropping {
  aspectRatioX: number;
  aspectRatioY: number;
  autoRotateAspectRatio: boolean;
  canIncreaseImageQuantity: boolean;
}

export interface IProduct {
  id: string;
  laboratory: string;
  title: {
    en: string;
    de: string;
  };
  dimensions: IProductDimensions;
  _productGroup: ProductGroup;
  tags: IDictionary<string>;
  bulletPoints: string[];
  description: string;
  images: IProductImage[];
  options: IOption[];
  perfectFor: IPerfectFor[];
  price: number;
  priceTotal: number;
  tax: number;
  currency: string;
  slug: string;
  minImageSelection: number;
  maxImageSelection: number;
  maxPages?: number;
  cropping: ICropping;
  productGroupId: string;
}

export interface IPriceItem {
  active: boolean;
  additionalImagePriceTotal: number;
  additionalPageResellerPriceTotal: number;
  additionalImageResellerPriceTotal: number;
  allowEditAdditionalImage: boolean;
  allowEditAdditionalPage: boolean;
  currency: Currency;
  priceTotal: number;
  productName: string;
  resellerPrice: number;
  resellerPriceTotal: number;
  _id: string;
  imagesIncluded?: number;
  additionalPagePriceTotal?: number;
  pagesIncluded?: number;
  pagesIncludedReseller?: number;
  _product: IProduct;
  name?: string;
  allImagesIncluded?: boolean;
  tier?: number;
  bestseller?: boolean;
}

export enum SHOP_ORDER_STATUS {
  PENDING = 'pending',
  DRAFT = 'draft',
  PAYMENT_PENDING = 'payment_pending',
  PAYMENT_COMPLETE = 'payment_complete',
  CROP_AND_STORE_IMAGES = 'crop_and_store_images',
  ERROR_CROP_AND_STORE_IMAGES = 'error_crop_and_store_images',
  FULFILLMENT_PENDING = 'fulfillment_pending',
  SENDING_TO_LAB = 'sending_to_lab',
  ERROR_SENDING_TO_LAB = 'error_sending_to_lab',
  MANUFACTURING_PENDING = 'manufacturing_pending',
  DELIVERY_PENDING = 'delivery_pending',
  HOLD = 'hold',
  COMPLETE = 'complete',
  CANCELED = 'canceled'
}

export enum SHOP_PAYMENT_METHODS {
  PAYPAL = 'paypal',
  IBAN = 'iban',
  CARD = 'card',
  SEPA = 'sepa',
  NONE = 'none'
}

export type OrderShippingDetails = {
  countryCode: string;
  countryName: string;
  firstName: string;
  lastName: string;
  street: string;
  streetNumber: string;
  zipCode: string;
  city: string;
};

export enum SHOP_CURRENCIES {
  EUR = 'EUR',
  USD = 'USD',
  CHF = 'CHF',
  GBP = 'GBP'
}

export interface ProfitBreakdownItem {
  reason: string;
  amount: number;
}

export interface IUserEarnings {
  items: ProfitBreakdownItem[];
  earnings: number;
  taxEarnings: number;
  grossEarnings: number;
}

export interface IOrderBoundsRelative {
  widthPercent: number;
  heightPercent: number;
  leftPercent: number;
  topPercent: number;
}

export interface IShopOrder {
  id: string;
  collection: string;
  orderNumber: number;
  grandTotal: number;
  payments: string[];
  status: SHOP_ORDER_STATUS;
  billing: {
    method: SHOP_PAYMENT_METHODS;
  };
  payinProcessed?: boolean;
  paid: boolean;
  shipping: OrderShippingDetails;
  createdAt: Date;
  currency: SHOP_CURRENCIES;
  collectionTitle: string;
  invoiceNumbers: string[];
  invoiceDownloadLink: string;
  userEarnings?: IUserEarnings;
  endCustomerInfo: {
    email?: string;
    phone?: string;
  };
  products: Array<{
    images: Array<unknown>;
    photobookId?: string;
    title?: string;
    price?: number;
    priceTotal?: number;
    quantity?: number;
    _product?: string;
    _productGroup?: string;
    productDetails?: {
      color?: string;
      coverImage?: {
        id: string;
        boundsRelative?: IOrderBoundsRelative;
        frameIndex?: number;
      };
      coverFirstTitle?: string;
      coverFirstTitleFont?: string;
      coverSecondTitle?: string;
      coverSecondTitleFont?: string;
      coverTextPosition?: string;
    };
    spreads?: Array<{
      index: number;
      frameLayout: string;
      images: Array<{
        id: string;
        frameIndex: number;
        boundsRelative?: IOrderBoundsRelative;
      }>;
    }>;
  }>;
}

export interface IFetchOrderListForUserResponse {
  list: IShopOrder[];
  meta: {
    totalAmount: number;
  };
  someOrdersArePaid: boolean;
}

export const CURRENCIES_SYMBOLS: { [key in SHOP_CURRENCIES]: string } = {
  [SHOP_CURRENCIES.USD]: '$',
  [SHOP_CURRENCIES.EUR]: '€',
  [SHOP_CURRENCIES.GBP]: '£',
  [SHOP_CURRENCIES.CHF]: 'Fr.'
};

export enum ShopCouponTypesEnum {
  Total = 'total',
  Product = 'product',
  ProductGroup = 'productGroup',
  Shipping = 'shipment'
}

export interface IShopCouponDiscount {
  type: ShopCouponTypesEnum;
  valueType: string;
  productId?: string | null;
  productGroupId?: string | null;
  quantityMax?: number | null;
  valueFixed: number;
  valuePercent: number;
}

export interface IShopCoupon {
  id?: string;
  user: string;
  name: string;
  description?: string;
  active: boolean;
  code: string;
  codeLowerCase?: string;
  validUntilDate: string;
  isUsedInSalesAutomation: boolean;
  minimumOrderValue?: number | null;
  discounts: IShopCouponDiscount[];
}

export interface IBalanceItem {
  amount: number;
  pendingPayout: boolean;
  minBalanceAmountForPayout: number;
}

export interface IUserAccountBalance {
  balance: IBalanceItem;
  pendingPayout: IBalanceItem;
}

export interface IPayDocument {
  date: string;
  url: string;
  orderNumbers: number[];
  orderIds: string[];
  reason: string;
  status: PAY_DOCUMENT_STATUS | null;
  blocksAccountUntilPaid: boolean;
  creditNotes: CreditNote[];
}

export interface CreditNote {
  id: string;
  providerId: string | null;
  url: string | null;
  createdAt: string;
  amountGross: number;
  amountNet: number;
  amountTax: number;
  lines: Array<{
    orderId: string;
  }>;
}

export enum PAY_DOCUMENT_STATUS {
  OPEN = 'open',
  PENDING = 'pending',
  FAILED = 'failed',
  PAID = 'paid'
}

export interface IShopReducerState {
  pricingList: IPricingListItem[];
  pricingGroups: IPricingGroup[];
  payDocuments: IPayDocumentsList;
  userHasEnoughInvoicingData: boolean | null;
  selectedPriceList: string;
  productUserPricing: IPriceItem[];
  userBalance: IUserAccountBalance;
  coupons: IShopCoupon[];
  orders: IFetchOrderListForUserResponse;
  orderImages: OrderImageForUser[];
  isCouponsFetched: boolean;
  productGroups: ProductGroup[];
  products: IProduct[];
  laboratories: Laboratory[];
  globalSettings: null | {
    id: string;
    userId: string;
    printShopActive: boolean;
  };
  collectionsSearchResult: ISearchCollection[];
  salesAutomation: {
    bulkUpdateInProgress: boolean;
  };
}

export type OrderImageForUser = {
  imageUrl: string;
  quantity: number;
  productTitle: string;
  productDimensions?: string;
  selectedOptions: string[];
  grandGrossPrice: number;
};

export interface IUpdatePriceValue {
  id: string;
  local?: boolean;
  data: {
    key: string;
    value: number | string | boolean;
  };
}

export interface IUpdatePricingGroup {
  id: string;
  _laboratory?: string;
  favorite?: boolean;
  active?: boolean;
}

export interface ICreateDigitalProduct {
  pricingListId: string;
  name: string;
  priceInCents: number;
  imagesIncluded: number;
  additionalImagePrice: number;
  allImages: boolean;
  tier: number;
  bestseller: boolean;
}
export interface IDeleteDigitalUserPricing {
  pricingListId: string;
  userPricingId: string;
}

interface IUpdateProductUserPricing {
  id: string;
  updates: Array<{ key: string; value: number | string | boolean }>;
}
export interface IUpdateProductUserPricings {
  pricingListId: string;
  productUserPricings: IUpdateProductUserPricing[];
}
export type OrderCustomerInfo = OrderShippingDetails & {
  email?: string;
  phone?: string;
};

export interface IGlobalShopSettings {
  id: string;
  printShopActive: boolean;
  userId: string;
}

export enum ShopCurrenciesEnum {
  EUR = 'EUR' /* ,
	USD = "USD",
	CHF = "CHF",
	GBP = "GBP" */
}

export type TShopCurrencies = typeof ShopCurrenciesEnum;

export enum ShopCouponValueTypeEnum {
  Fixed = 'fixed',
  Percent = 'percent'
}

export interface IShopSalesAutomation {
  couponCodeAdvertising: {
    enabled: boolean;
    startInDays: number | null;
    endsAfterDays: number | null;
    endsAt: string | null;
    startsAt: string | null;
    couponId: string | null;
    amountOfImagesBetweenBanners?: string | number | null;
  };
  anniversaryCouponCodeAdvertising: {
    enabled: boolean;
    anniversaryDate: string | null;
    couponId: string | null;
    amountOfImagesBetweenBanners?: string | number | null;
  };
  abandonedShoppingCartReminder: {
    enabled: boolean;
  };
  emptyCartProductSuggestion: {
    enabled: boolean;
  };
  freeShipping: {
    enabled: boolean;
    startsFrom: number | null;
  };
  couponCodeAfterPurchase: {
    enabled: boolean;
    couponId: string | null;
  };
  galleryExpires: {
    enabled: boolean;
    expiresInDays: number | null;
    expirationDate: Date | null;
  };
  emailPackage: string;
  marketingPhotobooks?: {
    enabled: boolean;
  };
}

export interface ProductGroupImage {
  src: string;
  type: string;
}

export interface ProductGroupTitle {
  en: string;
  de: string;
}

export interface ProductGroup {
  _id: string;
  id: string;
  images: ProductGroupImage[];
  order: number;
  slug: string;
  title: ProductGroupTitle;
  couponTitle: ProductGroupTitle;
}

export interface IFetchOrderListForUserParams {
  limit?: number;
  skip?: number;
  filters: {
    dateRange?: {
      start?: string;
      end?: string;
    };
  };
}

export interface ICancelOrderParams {
  orderId: string;
}

// Removed duplicate IFetchOrderListForUserResponse - see definition at line 248

export type Laboratory = {
  id: string;
  name: string;
  default: boolean;
};

export interface IRequestPayoutAction {
  amount: number;
  currency: string;
  account: string;
}

export interface ISearchCollection {
  _id: string;
  title: string;
}

export interface ISalesConfigBulkUpdateAction {
  collectionsToExclude: string[];
  printShopPricingId?: string;
  salesConfig: Partial<IShopSalesAutomation>;
  galleryTags?: IGalleryTag[];
}

export interface IShopPayDocumentsAction {
  skip?: number;
  limit?: number;
}

export interface IPayDocumentsList extends APIList<IPayDocument> {}

export interface APIList<TItem> {
  list: TItem[];
  meta: {
    skip?: number;
    limit?: number;
    totalAmount?: number;
  };
}
